import moment from "moment";
import {
  TradeConfirmationRow,
  DetailedWithdrawRequestRow,
  PurchaseHistoryRow,
  BondMetadataRow,
  UserBankInfo,
  CustomerData,
  BondResultRow,
  StockResultRow,
  CashBalanceRow,
  MissingSheet8UsdEntry,
  BalanceHistoryRow,
  BalanceHistoryEntry,
  RefundHistoryEntry,
} from "./types";
import { DEFAULT_BOND_METADATA, normalizeBondName, floorTo2Decimals, safeNumber } from "./utils";
import { BondRetakeRowWithTailbar } from "./generators";

// Build bond metadata map from saved metadata or defaults
export const buildBondMetaMap = (
  savedBondMetadata: BondMetadataRow[] | null
): Record<string, { rate: number; paymentDates: string[]; maturityDate?: string }> => {
  const bondMetaMap: Record<string, { rate: number; paymentDates: string[]; maturityDate?: string }> = {};
  if (savedBondMetadata && savedBondMetadata.length > 0) {
    savedBondMetadata.forEach((bond) => {
      const data = { rate: bond.rate, paymentDates: bond.paymentDates || [], maturityDate: bond.maturityDate || "" };
      // Store under original name (lowercased) for exact matching
      const originalKey = bond.bondName.toLowerCase().trim();
      bondMetaMap[originalKey] = data;
      // Also store under normalized key as fallback
      const normalizedKey = normalizeBondName(bond.bondName);
      if (!bondMetaMap[normalizedKey]) {
        bondMetaMap[normalizedKey] = data;
      }
    });
  } else {
    Object.entries(DEFAULT_BOND_METADATA).forEach(([name, meta]) => {
      bondMetaMap[name] = { rate: meta.rate, paymentDates: meta.paymentDates || [] };
    });
  }
  return bondMetaMap;
};

// Build TC price map from combined trade confirmations
export const buildTCPriceMap = (
  combinedTC: TradeConfirmationRow[]
): Record<string, number> => {
  const map: Record<string, number> = {};
  const parseTcDate = (value: string): number => {
    const parsed = moment(value, ["M/D/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"], true);
    return parsed.isValid() ? parsed.valueOf() : 0;
  };
  const latestBySymbol: Record<string, { unitPrice: number; sortValue: number }> = {};
  combinedTC.forEach(tc => {
    const symbol = (tc.symbol || "").trim();
    if (!symbol) return;
    const sortValue = Math.max(parseTcDate(tc.tradeDate), parseTcDate(tc.settlementDate));
    if (!latestBySymbol[symbol] || sortValue >= latestBySymbol[symbol].sortValue) {
      latestBySymbol[symbol] = { unitPrice: tc.unitPrice, sortValue };
    }
  });
  Object.entries(latestBySymbol).forEach(([symbol, entry]) => {
    map[symbol] = entry.unitPrice;
  });
  return map;
};

export interface ProcessedData {
  customerMap: Record<string, CustomerData>;
  bondRows: BondResultRow[];
  maturedBondRows: BondResultRow[];
  stockRows: StockResultRow[];
}

// Process withdraw data - main bond/stock calculation loop
export const processWithdrawData = (
  detailedWithdrawData: DetailedWithdrawRequestRow[],
  bankInfoMap: Record<string, UserBankInfo>,
  bondMetaMap: Record<string, { rate: number; paymentDates: string[]; maturityDate?: string }>,
  tcPriceMap: Record<string, number>,
  interestEndDate: string,
  usdRate: number,
  _purchaseHistoryData?: PurchaseHistoryRow[],
  balanceHistoryMap?: Record<string, BalanceHistoryEntry[]>,
  refundHistoryMap: Record<string, RefundHistoryEntry[]> = {},
): ProcessedData => {
  const customerMap: Record<string, CustomerData> = {};
  const bondRows: BondResultRow[] = [];
  const maturedBondRows: BondResultRow[] = [];
  const stockRows: StockResultRow[] = [];

  type BondPurchaseCandidate = {
    email: string;
    bondName: string;
    normalizedBondName: string;
    principleAmount: number;
    purchaseDate: string; // YYYY-MM-DD
    used: boolean;
  };

  const parseCreatedAtToDate = (createdAt: string): string => {
    const asNumber = Number(createdAt);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      return moment(asNumber).format("YYYY-MM-DD");
    }
    const parsed = moment(createdAt, [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY/MM/DD HH:mm:ss",
      "M/D/YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "M/D/YYYY",
      "MM/DD/YYYY",
    ], true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
    return "";
  };

  const bondCandidatesByEmail: Record<string, BondPurchaseCandidate[]> = {};
  if (balanceHistoryMap) {
    Object.entries(balanceHistoryMap).forEach(([emailRaw, history]) => {
      const email = emailRaw.toLowerCase().trim();
      const candidates: BondPurchaseCandidate[] = [];
      history.forEach((entry) => {
        const purchaseDate = parseCreatedAtToDate(entry.createdAt);
        (entry.bonds || []).forEach((bond) => {
          const bondName = bond.bond?.name || "";
          const amount = Number(bond.principleAmount) || 0;
          if (!bondName || amount <= 0 || !purchaseDate) return;
          candidates.push({
            email,
            bondName,
            normalizedBondName: normalizeBondName(bondName),
            principleAmount: amount,
            purchaseDate,
            used: false,
          });
        });
      });
      if (candidates.length > 0) {
        bondCandidatesByEmail[email] = candidates;
      }
    });
  }

  const pickBestBondCandidate = (
    email: string,
    withdrawBondName: string,
    withdrawAmount: number,
  ): BondPurchaseCandidate | null => {
    const candidates = bondCandidatesByEmail[email] || [];
    if (candidates.length === 0) return null;

    const normalizedWithdrawName = normalizeBondName(withdrawBondName);
    const available = candidates.filter(c => !c.used);
    const sameName = available.filter(c =>
      c.normalizedBondName === normalizedWithdrawName ||
      c.bondName.toLowerCase().trim() === withdrawBondName.toLowerCase().trim()
    );
    if (sameName.length === 0) return null;

    let best: BondPurchaseCandidate | undefined;
    let bestScore = Number.POSITIVE_INFINITY;
    for (const candidate of sameName) {
      const score = Math.abs(candidate.principleAmount - withdrawAmount);
      if (score < bestScore) {
        bestScore = score;
        best = candidate;
        continue;
      }
      if (score === bestScore && best) {
        const candidateDate = moment(candidate.purchaseDate, ["YYYY-MM-DD", "YYYY/MM/DD"], true);
        const bestDate = moment(best.purchaseDate, ["YYYY-MM-DD", "YYYY/MM/DD"], true);
        // Tie-breaker: prefer more recent purchase to match worker output behavior.
        if (candidateDate.isValid() && (!bestDate.isValid() || candidateDate.isAfter(bestDate))) {
          best = candidate;
        }
      }
    }
    if (!best) return null;
    best.used = true;
    return best;
  };

  const parseSupportedDate = (value: string): moment.Moment | null => {
    const parsed = moment(value, [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY/MM/DD HH:mm:ss",
      "M/D/YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "M/D/YYYY",
      "MM/DD/YYYY",
      "DD/MM/YYYY",
    ], true);
    return parsed.isValid() ? parsed.startOf("day") : null;
  };

  const buildBasePaymentSchedule = (
    paymentDates: string[],
    originalPurchaseDateObj: moment.Moment,
    interestEndDateObj: moment.Moment,
  ): moment.Moment[] => {
    if (paymentDates.length === 0) return [];

    const explicitDates = paymentDates
      .filter((dateStr) => /\d{4}/.test(dateStr))
      .map((dateStr) => parseSupportedDate(dateStr))
      .filter((item): item is moment.Moment => item !== null)
      .sort((a, b) => a.valueOf() - b.valueOf());

    if (explicitDates.length > 0) {
      return explicitDates.filter((date) => date.isAfter(originalPurchaseDateObj, "day"));
    }

    const monthDayPairs = paymentDates
      .map((dateStr) => {
        const parsed = moment(dateStr, ["M/D", "MM/DD", "M-D", "MM-DD"], true);
        if (!parsed.isValid()) return null;
        return { month: parsed.month(), day: parsed.date() };
      })
      .filter((item): item is { month: number; day: number } => item !== null);

    const dates: moment.Moment[] = [];
    const startYear = originalPurchaseDateObj.year() - 1;
    const endYear = interestEndDateObj.year() + 2;
    for (let year = startYear; year <= endYear; year++) {
      monthDayPairs.forEach(({ month, day }) => {
        dates.push(moment({ year, month, day }).startOf("day"));
      });
    }

    return dates
      .filter((date) => date.isAfter(originalPurchaseDateObj, "day"))
      .sort((a, b) => a.valueOf() - b.valueOf());
  };

  const shouldUseShiftedCouponDates = (email: string): boolean => {
    const cutoff = moment("2026-01-01", "YYYY-MM-DD", true);
    const refunds = refundHistoryMap[email.toLowerCase().trim()] || [];
    return refunds.some((entry) => {
      const createdAt = moment(Number(entry.createdAt));
      if (!createdAt.isValid()) return false;
      return createdAt.isBefore(cutoff, "day") && Number(entry.bondCount) === 0;
    });
  };

  const getEffectiveBondPurchaseDate = (
    originalPurchaseDate: string,
    paymentDates: string[],
    interestEndDateStr: string,
    useShiftedCouponDates: boolean,
  ): string => {
    const originalPurchaseDateObj = parseSupportedDate(originalPurchaseDate);
    const interestEndDateObj = parseSupportedDate(interestEndDateStr);
    if (!originalPurchaseDateObj || !interestEndDateObj) return originalPurchaseDate;
    if (paymentDates.length === 0) return originalPurchaseDateObj.format("YYYY-MM-DD");

    const baseSchedule = buildBasePaymentSchedule(paymentDates, originalPurchaseDateObj, interestEndDateObj);
    if (baseSchedule.length === 0) return originalPurchaseDateObj.format("YYYY-MM-DD");

    let effective = originalPurchaseDateObj.clone();
    let previousBase: moment.Moment | null = null;
    let previousAdjusted: moment.Moment | null = null;

    baseSchedule.forEach((baseDate, index) => {
      let adjustedDate = baseDate.clone();
      if (useShiftedCouponDates && index > 0 && previousBase && previousAdjusted) {
        const gapDays = Math.max(0, baseDate.diff(previousBase, "days"));
        adjustedDate = previousAdjusted.clone().add(gapDays + 1, "days");
      }

      if (
        adjustedDate.isBefore(interestEndDateObj, "day") &&
        adjustedDate.isAfter(effective, "day")
      ) {
        effective = adjustedDate.clone();
      }

      previousBase = baseDate.clone();
      previousAdjusted = adjustedDate.clone();
    });

    return effective.format("YYYY-MM-DD");
  };

  detailedWithdrawData.forEach(row => {
    const email = row.email.toLowerCase().trim();
    const userInfo = bankInfoMap[email] || {};

    if (!customerMap[email]) {
      customerMap[email] = {
        email,
        lastName: row.lastName,
        firstName: row.firstName,
        regNumber: row.regNumber,
        phone: row.phone,
        ttd: row.ttd,
        bankName: userInfo.bankName || "",
        accountNo: userInfo.accountNo || "",
        depositCode: userInfo.depositCode || "",
        bondTotal: 0,
        stockTotal: 0,
        cashTotal: userInfo.cashTotal || 0,
      };
    }

    const customer = customerMap[email];

    if (row.assetType === "Бонд") {
      const requestAmount = row.quantity;
      const normalizedName = normalizeBondName(row.assetName);
      // Try exact match first (lowercased original name), then normalized
      const exactKey = row.assetName.toLowerCase().trim();
      const bondMeta = bondMetaMap[exactKey] || bondMetaMap[normalizedName];

      // Worker parity: skip unknown bond names that do not exist in metadata.
      if (!bondMeta) {
        return;
      }

      const matchedCandidate = pickBestBondCandidate(email, row.assetName, requestAmount);
      // Worker rule: Sheet 2 "Мөнгөн дүн" must come from the uploaded
      // withdraw-request file's I column ("Тоо ширхэг"), not from balance history.
      const amount = requestAmount;
      const bondRate = bondMeta?.rate || 0.20;
      const paymentDates = bondMeta?.paymentDates || [];

      const interestEndDateObj = parseSupportedDate(interestEndDate) || moment(interestEndDate, "YYYY-MM-DD", true);
      const originalPurchaseDate = row.purchaseDate || matchedCandidate?.purchaseDate || "";
      const useShiftedCouponDates = shouldUseShiftedCouponDates(email);
      const effectivePurchaseDate = getEffectiveBondPurchaseDate(
        originalPurchaseDate,
        paymentDates,
        interestEndDate,
        useShiftedCouponDates,
      );

      const effectivePurchaseDateObj = parseSupportedDate(effectivePurchaseDate) || moment(effectivePurchaseDate, "YYYY-MM-DD", true);

      // Check if bond is matured: the bond's maturity date has passed (is on or before interestEndDate)
      const maturityDateStr = bondMeta?.maturityDate || "";
      const maturityDateObj = maturityDateStr ? moment(maturityDateStr, ["M/D/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "YYYY/MM/DD"], true) : null;
      const isMatured = maturityDateObj !== null && maturityDateObj.isValid() &&
        maturityDateObj.isSameOrBefore(interestEndDateObj, "day");

      // For matured bonds: 0 days, interestEndDate = effectivePurchaseDate
      const actualInterestEndDate = isMatured ? effectivePurchaseDate : interestEndDate;
      const days = isMatured ? 0 : Math.max(0, interestEndDateObj.diff(effectivePurchaseDateObj, "day"));

      // Formula: interest = amount * rate * days / 365
      const calculatedInterest = amount * bondRate * days / 365;
      // 100% of calculated interest is added to total
      const totalPrice = amount + calculatedInterest;
      // Matured bonds: no fees, full amount returned
      const avtamataFee = isMatured ? 0 : row.avtamataFee;
      const tradingFee = 0; // Арилжааны шимтгэл
      const oat = isMatured ? 0 : calculatedInterest * 0.10; // 10% tax on interest
      const bondTransferAmount = totalPrice - avtamataFee - tradingFee - oat;

      const bondRow: BondResultRow = {
        email,
        phone: row.phone,
        lastName: customer.lastName,
        firstName: customer.firstName,
        regNumber: row.regNumber,
        bankAccount: customer.accountNo,
        bank: customer.bankName,
        txCode: customer.depositCode,
        ttd: row.ttd,
        name: row.assetName,
        amount: amount,
        bondRate: bondRate,
        purchaseDate: effectivePurchaseDate,
        sourcePurchaseDate: originalPurchaseDate,
        interestEndDate: actualInterestEndDate,
        days: days,
        calculatedInterest: calculatedInterest,
        tax30: calculatedInterest,
        totalPrice: totalPrice,
        avtamataFee: avtamataFee,
        oat10: oat,
        transferAmount: bondTransferAmount,
      };

      // Matured bonds (0 days = already matured) go to separate sheet
      if (days === 0) {
        maturedBondRows.push(bondRow);
      } else {
        bondRows.push(bondRow);
      }

      customer.bondTotal += bondTransferAmount;

    } else if (row.assetType === "Хувьцаа") {
      const quantity = row.quantity;
      const symbol = row.assetName;
      const tcPrice = tcPriceMap[symbol];
      const avtamataFee = row.avtamataFee;

      let price: number;
      let totalMNT: number;
      let uckFee: number;
      let stockTransferAmount: number;

      if (tcPrice) {
        price = tcPrice;
        totalMNT = quantity * price * usdRate;
        uckFee = totalMNT * 0.008;
        stockTransferAmount = totalMNT - uckFee - avtamataFee;
      } else {
        totalMNT = row.totalPrice;
        price = quantity > 0 ? totalMNT / quantity / usdRate : 0;
        uckFee = row.uckFee;
        stockTransferAmount = row.transferAmount;
      }

      stockRows.push({
        email,
        phone: row.phone,
        lastName: customer.lastName,
        firstName: customer.firstName,
        regNumber: row.regNumber,
        bankAccount: customer.accountNo,
        bank: customer.bankName,
        txCode: customer.depositCode,
        ttd: row.ttd,
        purchaseDate: row.purchaseDate,
        symbol: symbol,
        name: row.assetName,
        quantity: quantity,
        price,
        usdRate,
        totalMNT: totalMNT,
        uckFee: uckFee,
        avtamataFee: avtamataFee,
        transferAmount: stockTransferAmount,
      });

      customer.stockTotal += floorTo2Decimals(stockTransferAmount);
    }
  });

  return { customerMap, bondRows, maturedBondRows, stockRows };
};

// Process cash balance rows for Sheet 8
export const processCashBalanceRows = (
  balanceHistoryMap: Record<string, BalanceHistoryEntry[]>,
  customerMap: Record<string, CustomerData>,
  bankInfoMap: Record<string, UserBankInfo>,
  portfolioAllocationsMap: Record<number, Record<string, number>>,
  purchaseHistoryData: PurchaseHistoryRow[] = [],
  tradeConfirmations: TradeConfirmationRow[] = [],
  manualUsdRateMap: Record<string, number> = {},
): { cashBalanceRows: CashBalanceRow[]; missingUsdEntries: MissingSheet8UsdEntry[] } => {
  const cashBalanceRows: CashBalanceRow[] = [];
  const WORKER_ALLOCATION_STEP = 0.025;

  const normalizeDateKey = (dateStr: string): string => {
    if (!dateStr) return "";
    const parsed = moment(dateStr, [
      "YYYY/MM/DD HH:mm:ss",
      "YYYY/MM/DD",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
      "M/D/YYYY",
      "MM/DD/YYYY",
    ], true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
    const asNumber = Number(dateStr);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      const ts = moment(asNumber);
      if (ts.isValid()) return ts.format("YYYY-MM-DD");
    }
    return "";
  };

  const purchaseHistoryEntriesByEmail = new Map<string, {
    amount: number;
    portfolioIndex: number;
    bondAmount: number;
    holdAmount: number;
    purchaseDate: string;
    normalizedDate: string;
  }[]>();
  purchaseHistoryData.forEach((row) => {
    const email = (row.email || "").toLowerCase().trim();
    const dateKey = normalizeDateKey(row.haDate || "");
    const portfolioIndex = Number(row.portfolioIndex) || 0;
    if (!email.includes("@") || !dateKey || portfolioIndex <= 0) return;
    if (!purchaseHistoryEntriesByEmail.has(email)) purchaseHistoryEntriesByEmail.set(email, []);
    purchaseHistoryEntriesByEmail.get(email)!.push({
      amount: Number(row.amount) || 0,
      portfolioIndex,
      bondAmount: Number(row.bond) || 0,
      holdAmount: Number(row.holdAmount) || 0,
      purchaseDate: row.haDate || dateKey,
      normalizedDate: dateKey,
    });
  });

  const findHistoricalPurchaseMatch = (
    email: string,
    purchaseDate: string,
    principleAmount: number,
  ): { amount: number; portfolioIndex: number; bondAmount: number; holdAmount: number; purchaseDate: string } | null => {
    const normalizedPurchaseDate = normalizeDateKey(purchaseDate);
    const rows = purchaseHistoryEntriesByEmail.get(email.toLowerCase().trim()) || [];
    if (rows.length === 0) return null;

    const purchaseMoment = normalizedPurchaseDate ? moment(normalizedPurchaseDate, "YYYY-MM-DD", true) : null;
    const candidates = rows.filter((row) => {
      if (row.normalizedDate === normalizedPurchaseDate) return true;
      if (!purchaseMoment) return false;
      const rowMoment = moment(row.normalizedDate, "YYYY-MM-DD", true);
      if (!rowMoment.isValid()) return false;
      return Math.abs(rowMoment.diff(purchaseMoment, "days")) <= 1;
    });
    if (candidates.length === 0) return null;
    if (candidates.length === 1) {
      return {
        amount: candidates[0].amount,
        portfolioIndex: candidates[0].portfolioIndex,
        bondAmount: candidates[0].bondAmount,
        holdAmount: candidates[0].holdAmount,
        purchaseDate: candidates[0].purchaseDate,
      };
    }
    const targetAmount = Number(principleAmount) || 0;
    const best = candidates.reduce((acc, current) => {
      if (!acc) return current;
      const currentDateGap = purchaseMoment
        ? Math.abs(moment(current.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      const accDateGap = purchaseMoment
        ? Math.abs(moment(acc.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      const currentAmountGap = Math.abs(current.amount - targetAmount);
      const accAmountGap = Math.abs(acc.amount - targetAmount);
      if (currentDateGap !== accDateGap) return currentDateGap < accDateGap ? current : acc;
      return currentAmountGap < accAmountGap ? current : acc;
    }, candidates[0]);
    if (!best) return null;
    return {
      amount: best.amount,
      portfolioIndex: best.portfolioIndex,
      bondAmount: best.bondAmount,
      holdAmount: best.holdAmount,
      purchaseDate: best.purchaseDate,
    };
  };

  const findBondPurchaseMatch = (
    email: string,
    purchaseDate: string,
    bondAmount: number,
  ): { amount: number; portfolioIndex: number; bondAmount: number; holdAmount: number; purchaseDate: string } | null => {
    const normalizedPurchaseDate = normalizeDateKey(purchaseDate);
    const rows = purchaseHistoryEntriesByEmail.get(email.toLowerCase().trim()) || [];
    const targetBondAmount = Number(bondAmount) || 0;
    if (rows.length === 0 || targetBondAmount <= 0) return null;

    const exactBondRows = rows.filter((row) => Math.abs(row.bondAmount - targetBondAmount) <= 1);
    if (exactBondRows.length === 0) return null;

    const purchaseMoment = normalizedPurchaseDate ? moment(normalizedPurchaseDate, "YYYY-MM-DD", true) : null;
    const best = exactBondRows.reduce((acc, current) => {
      if (!acc) return current;
      const currentDateGap = purchaseMoment
        ? Math.abs(moment(current.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      const accDateGap = purchaseMoment
        ? Math.abs(moment(acc.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      if (currentDateGap !== accDateGap) return currentDateGap < accDateGap ? current : acc;
      const currentAmountGap = Math.abs(current.amount - targetBondAmount);
      const accAmountGap = Math.abs(acc.amount - targetBondAmount);
      return currentAmountGap < accAmountGap ? current : acc;
    }, exactBondRows[0]);

    return {
      amount: best.amount,
      portfolioIndex: best.portfolioIndex,
      bondAmount: best.bondAmount,
      holdAmount: best.holdAmount,
      purchaseDate: best.purchaseDate,
    };
  };

  const inferPortfolioIndexForPurchase = (
    purchase: BalanceHistoryEntry,
    fallbackPortfolioIndex: number,
  ): number => {
    const investedAmount = Number(purchase.principleAmount) || 0;
    if (investedAmount <= 0) return fallbackPortfolioIndex;

    const observedStockRatioBySymbol: Record<string, number> = {};
    (purchase.stocks || []).forEach((stock) => {
      const symbol = stock.stock?.symbol || "";
      if (!symbol) return;
      const amount = Number(stock.totalPrice) || 0;
      if (amount <= 0) return;
      observedStockRatioBySymbol[symbol] =
        (observedStockRatioBySymbol[symbol] || 0) + amount / investedAmount;
    });

    const observedBondAmount = (purchase.bonds || []).reduce(
      (sum, bond) => sum + (Number(bond.principleAmount) || 0),
      0,
    );
    const observedBondRatio = observedBondAmount > 0 ? observedBondAmount / investedAmount : 0;

    let bestIndex = fallbackPortfolioIndex;
    let bestError = Number.POSITIVE_INFINITY;

    Object.entries(portfolioAllocationsMap).forEach(([idxStr, allocations]) => {
      const idx = Number(idxStr);
      if (!Number.isFinite(idx)) return;

      const targetStockRatioBySymbol = allocations || {};
      const targetStockRatioSum = Object.values(targetStockRatioBySymbol).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0,
      );
      const targetBondRatio = Math.max(0, 1 - targetStockRatioSum);

      let error = Math.abs(observedBondRatio - targetBondRatio);

      const allSymbols = new Set<string>([
        ...Object.keys(observedStockRatioBySymbol),
        ...Object.keys(targetStockRatioBySymbol),
      ]);

      allSymbols.forEach((symbol) => {
        const observed = observedStockRatioBySymbol[symbol] || 0;
        const target = targetStockRatioBySymbol[symbol] || 0;
        error += Math.abs(observed - target);
      });

      if (error < bestError) {
        bestError = error;
        bestIndex = idx;
      }
    });

    return bestIndex;
  };

  const normalizeWorkerAllocation = (ratio: number): number => {
    const raw = safeNumber(ratio);
    if (raw <= 0) return 0;
    if (raw < WORKER_ALLOCATION_STEP / 2) return raw;
    return Math.round(raw / WORKER_ALLOCATION_STEP) * WORKER_ALLOCATION_STEP;
  };

  const getWorkerStylePortfolioAllocations = (portfolioIndex: number): Record<string, number> => {
    const rawAllocations = portfolioAllocationsMap[portfolioIndex] || {};
    const normalized: Record<string, number> = {};
    Object.entries(rawAllocations).forEach(([symbol, ratio]) => {
      const normalizedRatio = normalizeWorkerAllocation(Number(ratio) || 0);
      if (normalizedRatio > 0) {
        normalized[symbol] = normalizedRatio;
      }
    });
    return normalized;
  };

  const parseTcDate = (value: string): moment.Moment | null => {
    const raw = String(value || "").trim();
    if (!raw) return null;
    const numeric = Number(raw);
    if (Number.isFinite(numeric) && numeric > 0) {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + numeric * 24 * 60 * 60 * 1000);
      const parsedNumeric = moment(date);
      if (parsedNumeric.isValid()) return parsedNumeric;
    }
    const parsed = moment(raw, [
      "M/D/YYYY",
      "MM/DD/YYYY",
      "M/D/YYYY H:mm:ss",
      "MM/DD/YYYY H:mm:ss",
      "M/D/YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY/MM/DD HH:mm:ss",
    ], true);
    return parsed.isValid() ? parsed : null;
  };

  const formatTcDate = (value: string): string => {
    const parsed = parseTcDate(value);
    return parsed ? parsed.format("M/D/YYYY") : value;
  };

  const getUsdDateKey = (date: string): string =>
    parseTcDate(date)?.format("YYYY-MM-DD") || String(date || "").trim();

  const tradeConfirmationsBySymbol = new Map<string, TradeConfirmationRow[]>();
  const inferredUsdByDate = new Map<string, number>();
  tradeConfirmations.forEach((tc) => {
    const symbol = (tc.symbol || "").trim().toUpperCase();
    if (!symbol) return;
    if (!tradeConfirmationsBySymbol.has(symbol)) tradeConfirmationsBySymbol.set(symbol, []);
    tradeConfirmationsBySymbol.get(symbol)!.push(tc);
    const usdRate = safeNumber(tc.usdRate);
    if (usdRate <= 0) return;
    [tc.tradeDate, tc.settlementDate].forEach((dateValue) => {
      const usdKey = getUsdDateKey(dateValue);
      if (usdKey && !inferredUsdByDate.has(usdKey)) {
        inferredUsdByDate.set(usdKey, usdRate);
      }
    });
  });

  const missingUsdAccumulator = new Map<string, MissingSheet8UsdEntry>();
  const registerMissingUsdDate = (dateValue: string) => {
    const key = getUsdDateKey(dateValue);
    if (!key) return;
    if (safeNumber(manualUsdRateMap[key]) > 0 || safeNumber(inferredUsdByDate.get(key) || 0) > 0) return;
    if (!missingUsdAccumulator.has(key)) {
      missingUsdAccumulator.set(key, {
        key,
        date: formatTcDate(dateValue || "") || formatTcDate(key) || key,
      });
    }
  };

  type TcCandidate = {
    stockPrice: number;
    usdRate: number;
    paymentDate: string;
    purchasedAmountMNT: number;
    unitPriceMNT: number;
    fee: number;
    diff: number;
    tradeDate: string;
    settlementDate: string;
  };

  type PurchaseStockInput = {
    symbol: string;
    quantity: number;
    purchasedAmountMNT: number;
  };

  type PurchaseStockResolution = {
    stockPrice: number;
    usdRate: number;
    paymentDate: string;
    purchasedAmountMNT: number;
    unitPriceMNT: number;
    fee: number;
    custodyNeedsReview: boolean;
  };

  const buildTcCandidate = (
    tc: TradeConfirmationRow,
    quantity: number,
    purchasedAmountMNT: number,
    paymentDate: string,
    mode: "trade" | "settlement",
  ): TcCandidate | null => {
    const stockPrice = safeNumber(tc.unitPrice);
    if (stockPrice <= 0) return null;
    const selectedDate = mode === "trade" ? tc.tradeDate : tc.settlementDate;
    const usdKey = getUsdDateKey(selectedDate);
    if (safeNumber(tc.usdRate) <= 0) {
      registerMissingUsdDate(tc.tradeDate);
      registerMissingUsdDate(tc.settlementDate);
    }
    const usdRate =
      safeNumber(tc.usdRate) ||
      safeNumber(manualUsdRateMap[usdKey]) ||
      safeNumber(inferredUsdByDate.get(usdKey) || 0);
    if (usdRate <= 0) return null;
    const unitPriceMNT = stockPrice * usdRate;
    const computedPurchasedMNT = quantity * unitPriceMNT;
    return {
      stockPrice,
      usdRate,
      paymentDate,
      purchasedAmountMNT: computedPurchasedMNT,
      unitPriceMNT,
      fee: computedPurchasedMNT * 0.008,
      diff: Math.abs(computedPurchasedMNT - purchasedAmountMNT),
      tradeDate: tc.tradeDate || "",
      settlementDate: tc.settlementDate || "",
    };
  };

  const findBestCandidateForAnchor = (
    symbol: string,
    quantity: number,
    purchasedAmountMNT: number,
    anchorMoment: moment.Moment | null,
    mode: "trade" | "settlement",
  ): TcCandidate | null => {
    if (!anchorMoment) return null;
    const candidates = tradeConfirmationsBySymbol.get((symbol || "").trim().toUpperCase()) || [];
    const matchingRows = candidates.filter((tc) => {
      const anchorDate = mode === "trade" ? parseTcDate(tc.tradeDate) : parseTcDate(tc.settlementDate);
      return !!anchorDate && anchorDate.isSame(anchorMoment, "day");
    });
    if (matchingRows.length === 0) return null;

    const paymentDate = anchorMoment.format("M/D/YYYY");
    return matchingRows
      .map((tc) => buildTcCandidate(tc, quantity, purchasedAmountMNT, paymentDate, mode))
      .filter((item): item is TcCandidate => item !== null)
      .sort((a, b) => a.diff - b.diff)[0] || null;
  };

  const resolvePurchaseStockMatches = (
    email: string,
    purchaseDateStr: string,
    stockInputs: PurchaseStockInput[],
  ): PurchaseStockResolution[] => {
    const purchaseMoment = moment(purchaseDateStr, [
      "YYYY/MM/DD HH:mm:ss",
      "YYYY/MM/DD",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
    ], true);
    if (!purchaseMoment.isValid() || stockInputs.length === 0) {
      return stockInputs.map(() => ({
        stockPrice: 0,
        usdRate: 0,
        paymentDate: "",
        purchasedAmountMNT: 0,
        unitPriceMNT: 0,
        fee: 0,
        custodyNeedsReview: true,
      }));
    }

    const symbols = Array.from(new Set(stockInputs.map((item) => (item.symbol || "").trim().toUpperCase()).filter(Boolean)));
    const tradeDateSets = symbols.map((symbol) => {
      const keys = new Set<string>();
      (tradeConfirmationsBySymbol.get(symbol) || []).forEach((tc) => {
        const tradeMoment = parseTcDate(tc.tradeDate);
        if (!tradeMoment || !tradeMoment.isBefore(purchaseMoment, "day")) return;
        keys.add(tradeMoment.format("YYYY-MM-DD"));
      });
      return keys;
    });

    const intersectDateSets = (sets: Set<string>[]): string[] => {
      if (sets.length === 0) return [];
      const [first, ...rest] = sets;
      return Array.from(first).filter((key) => rest.every((set) => set.has(key)));
    };

    const commonTradeDateKeys = intersectDateSets(tradeDateSets).sort();
    const latestTradeMoment = commonTradeDateKeys.length > 0
      ? moment(commonTradeDateKeys[commonTradeDateKeys.length - 1], "YYYY-MM-DD", true)
      : symbols.reduce<moment.Moment | null>((best, symbol) => {
          const candidates = tradeConfirmationsBySymbol.get(symbol) || [];
          candidates.forEach((tc) => {
            const tradeMoment = parseTcDate(tc.tradeDate);
            if (!tradeMoment || !tradeMoment.isBefore(purchaseMoment, "day")) return;
            if (!best || tradeMoment.isAfter(best, "day")) {
              best = tradeMoment;
            }
          });
          return best;
        }, null);

    const settlementDateSets = symbols.map((symbol) => {
      const keys = new Set<string>();
      const candidates = tradeConfirmationsBySymbol.get(symbol) || [];
      candidates.forEach((tc) => {
        const tradeMoment = parseTcDate(tc.tradeDate);
        const settlementMoment = parseTcDate(tc.settlementDate);
        if (!settlementMoment || !settlementMoment.isSameOrBefore(purchaseMoment, "day")) return;
        if (latestTradeMoment && tradeMoment && !tradeMoment.isSame(latestTradeMoment, "day")) return;
        keys.add(settlementMoment.format("YYYY-MM-DD"));
      });
      return keys;
    });

    const commonSettlementDateKeys = intersectDateSets(settlementDateSets).sort();
    const anchorSettlementMoment = commonSettlementDateKeys.length > 0
      ? moment(commonSettlementDateKeys[commonSettlementDateKeys.length - 1], "YYYY-MM-DD", true)
      : null;

    const tradeCandidates = stockInputs.map((item) =>
      findBestCandidateForAnchor(
        item.symbol,
        item.quantity,
        item.purchasedAmountMNT,
        latestTradeMoment,
        "trade",
      ),
    );
    const settlementCandidates = stockInputs.map((item) =>
      findBestCandidateForAnchor(
        item.symbol,
        item.quantity,
        item.purchasedAmountMNT,
        anchorSettlementMoment,
        "settlement",
      ),
    );

    const isExact = (candidate: TcCandidate | null, purchasedAmountMNT: number): boolean =>
      !!candidate && candidate.diff <= Math.max(1, purchasedAmountMNT * 0.001);

    const tradeAllExact = stockInputs.every((item, index) => isExact(tradeCandidates[index], item.purchasedAmountMNT));
    const settlementAllExact = stockInputs.every((item, index) => isExact(settlementCandidates[index], item.purchasedAmountMNT));

    let chosenMode: "trade" | "settlement" | null = null;

    if (tradeAllExact) {
      chosenMode = "trade";
    } else if (settlementAllExact) {
      chosenMode = "settlement";
    } else {
      const tradeTotalDiff = tradeCandidates.reduce((sum, candidate) => sum + (candidate?.diff || Number.MAX_SAFE_INTEGER / 1000), 0);
      const settlementTotalDiff = settlementCandidates.reduce((sum, candidate) => sum + (candidate?.diff || Number.MAX_SAFE_INTEGER / 1000), 0);
      if (latestTradeMoment && (!anchorSettlementMoment || tradeTotalDiff <= settlementTotalDiff)) {
        chosenMode = "trade";
      } else if (anchorSettlementMoment) {
        chosenMode = "settlement";
      }
    }

    const commonPaymentDate = chosenMode === "trade"
      ? (latestTradeMoment ? formatTcDate(latestTradeMoment.format("YYYY-MM-DD")) : "")
      : (anchorSettlementMoment ? formatTcDate(anchorSettlementMoment.format("YYYY-MM-DD")) : "");

    return stockInputs.map((item, index) => {
      const tradeExact = isExact(tradeCandidates[index], item.purchasedAmountMNT);
      const settlementExact = isExact(settlementCandidates[index], item.purchasedAmountMNT);
      const chosenCandidate = chosenMode === "trade"
        ? (tradeCandidates[index] || settlementCandidates[index])
        : chosenMode === "settlement"
          ? (settlementCandidates[index] || tradeCandidates[index])
          : (tradeCandidates[index] || settlementCandidates[index]);

      return {
        stockPrice: chosenCandidate?.stockPrice || 0,
        usdRate: chosenCandidate?.usdRate || 0,
        paymentDate: commonPaymentDate,
        purchasedAmountMNT: chosenCandidate?.purchasedAmountMNT || 0,
        unitPriceMNT: chosenCandidate?.unitPriceMNT || 0,
        fee: chosenCandidate?.fee || 0,
        custodyNeedsReview: (!tradeExact && !settlementExact) || !chosenCandidate,
      };
    });
  };

  Object.entries(balanceHistoryMap).forEach(([email, balanceHistory]) => {
    const customer = customerMap[email];
    const userInfo = bankInfoMap[email] || {};
    const balanceHoldAmount = userInfo.holdAmount || 0;
    const totalHoldAmountFromHistory = balanceHistory.reduce((sum, h) => sum + (h.holdAmount || 0), 0);

    // Sheet 8 should only expand purchases whose purchase-level hold amount
    // has already gone to zero in dashboard purchase history.
    const eligiblePurchases = balanceHistory.filter((h) => {
      if (Math.abs(Number(h.holdAmount) || 0) > 0.000001) return false;
      if ((Number(h.principleAmount) || 0) <= 0) return false;
      if ((Number(h.fee) || 0) <= 0) return false;
      return (h.stocks || []).length > 0 || (h.bonds || []).length > 0;
    });

    eligiblePurchases.forEach(purchase => {
      const fallbackPortfolioIndex = userInfo.portfolioIndex || 0;
      const purchaseDateStr = moment(Number(purchase.createdAt)).format("YYYY/MM/DD HH:mm:ss");
      const historicalMatch = findHistoricalPurchaseMatch(
        email,
        purchaseDateStr,
        purchase.principleAmount,
      );
      const portfolioIndex = historicalMatch?.portfolioIndex ?? inferPortfolioIndexForPurchase(
        purchase,
        fallbackPortfolioIndex,
      );
      const investedAmount = historicalMatch ? (Number(historicalMatch.amount) || 0) : (purchase.principleAmount || 0);
      const portfolioAllocations = getWorkerStylePortfolioAllocations(portfolioIndex);

      if (purchase.stocks && purchase.stocks.length > 0) {
        const eligibleStockItems = purchase.stocks
          .map((stock) => ({ stock }))
          .filter(({ stock }) => !(
            stock.totalAmount <= 0 &&
            stock.totalPrice <= 0 &&
            stock.unitPrice <= 0 &&
            stock.fee <= 0 &&
            stock.holdAmount <= 0
          ))
          .map(({ stock }) => ({
            stock,
            symbol: stock.stock?.symbol || "",
            quantity: stock.totalAmount,
            purchasedAmountMNT: stock.totalPrice,
          }));

        const resolvedStockMatches = resolvePurchaseStockMatches(
          email,
          purchaseDateStr,
          eligibleStockItems.map((item) => ({
            symbol: item.symbol,
            quantity: item.quantity,
            purchasedAmountMNT: item.purchasedAmountMNT,
          })),
        );

        eligibleStockItems.forEach((item, stockIndex) => {
          const stock = item.stock;
          if (
            stock.totalAmount <= 0 &&
            stock.totalPrice <= 0 &&
            stock.unitPrice <= 0 &&
            stock.fee <= 0 &&
            stock.holdAmount <= 0
          ) return;

          const stockSymbol = stock.stock?.symbol || "";
          const stockPercentage = portfolioAllocations[stockSymbol] || 0;
          const calculatedAllocatedAmount = stockPercentage > 0 ? investedAmount * stockPercentage : stock.totalPrice;
          const tcMatch = resolvedStockMatches[stockIndex];
          const matchedPurchasedAmount = tcMatch && tcMatch.purchasedAmountMNT > 0 ? tcMatch.purchasedAmountMNT : stock.totalPrice;
          const matchedFee = tcMatch && tcMatch.fee > 0 ? tcMatch.fee : stock.fee;
          const calculatedCashBalance = calculatedAllocatedAmount - matchedPurchasedAmount - matchedFee;

          cashBalanceRows.push({
            lastName: customer?.lastName || "",
            firstName: customer?.firstName || "",
            regNumber: customer?.regNumber || "",
            email,
            purchaseType: purchase.title || "Production purchase",
            purchaseDate: purchaseDateStr,
            portfolioIndex: portfolioIndex,
            investedAmount,
            symbol: stockSymbol,
            description: stock.stock?.name || "",
            allocatedAmount: calculatedAllocatedAmount,
            quantity: stock.totalAmount,
            purchasedAmountMNT: matchedPurchasedAmount,
            unitPriceMNT: tcMatch && tcMatch.unitPriceMNT > 0 ? tcMatch.unitPriceMNT : stock.unitPrice,
            fee: matchedFee,
            cashBalance: calculatedCashBalance,
            stockPrice: tcMatch && tcMatch.stockPrice > 0 ? String(tcMatch.stockPrice) : "",
            usdRate: tcMatch && tcMatch.usdRate > 0 ? String(tcMatch.usdRate) : "",
            paymentDate: tcMatch?.paymentDate || "",
            custodyNeedsReview: tcMatch?.custodyNeedsReview || false,
            holdAmount: balanceHoldAmount,
            totalHoldAmount: totalHoldAmountFromHistory,
          });
        });
      }

      if (purchase.bonds && purchase.bonds.length > 0) {
        purchase.bonds.forEach(bond => {
          if (bond.principleAmount <= 0 && bond.fee <= 0) return;

          const bondSymbol = bond.bond?.symbol || "";
          const bondPercentage = portfolioAllocations[bondSymbol] || 0;
          const calculatedBondAmount = bondPercentage > 0 ? investedAmount * bondPercentage : bond.principleAmount;

          cashBalanceRows.push({
            lastName: customer?.lastName || "",
            firstName: customer?.firstName || "",
            regNumber: customer?.regNumber || "",
            email,
            purchaseType: purchase.title || "Production purchase",
            purchaseDate: purchaseDateStr,
            portfolioIndex: portfolioIndex,
            investedAmount,
            symbol: bondSymbol,
            description: bond.bond?.name || "",
            allocatedAmount: calculatedBondAmount,
            quantity: 0,
            purchasedAmountMNT: 0,
            unitPriceMNT: 0,
            fee: bond.fee,
            cashBalance: 0,
            stockPrice: "",
            usdRate: "",
            paymentDate: "",
            holdAmount: balanceHoldAmount,
            totalHoldAmount: totalHoldAmountFromHistory,
          });
        });
      }

      // No stocks or bonds - add single row if fee > 0
      if ((!purchase.stocks || purchase.stocks.length === 0) && (!purchase.bonds || purchase.bonds.length === 0)) {
        if (purchase.fee > 0) {
          cashBalanceRows.push({
            lastName: customer?.lastName || "",
            firstName: customer?.firstName || "",
            regNumber: customer?.regNumber || "",
            email,
            purchaseType: purchase.title || "Production purchase",
            purchaseDate: purchaseDateStr,
            portfolioIndex: portfolioIndex,
            investedAmount,
            symbol: "",
            description: purchase.title || "",
            allocatedAmount: investedAmount,
            quantity: 0,
            purchasedAmountMNT: 0,
            unitPriceMNT: 0,
            fee: purchase.fee,
            cashBalance: 0,
            stockPrice: "",
            usdRate: "",
            paymentDate: "",
            holdAmount: balanceHoldAmount,
            totalHoldAmount: totalHoldAmountFromHistory,
          });
        }
      }
    });
  });

  const missingUsdEntries = Array.from(missingUsdAccumulator.values()).sort((a, b) => a.key.localeCompare(b.key));

  return { cashBalanceRows, missingUsdEntries };
};

export interface Sheet4And6Result {
  balanceHistoryRows: BalanceHistoryRow[];
  bondRetakeRows: BondResultRow[];
}

// Determine tag based on ratio
const getTagFromRatio = (bondAmount: number, totalAmount: number): string => {
  if (totalAmount === 0) return "Бондын дахин худалдан авалт";
  const ratio = bondAmount / totalAmount;
  const roundedRatio = Math.round(ratio * 100) / 100;
  const isStandardRatio = roundedRatio >= 0 && roundedRatio <= 1 &&
    (Math.abs(roundedRatio * 20 - Math.round(roundedRatio * 20)) < 0.01);
  if (isStandardRatio) return "Normal";
  return "Бондын зөрүүтэй";
};

// Process balance history and bond retake rows (Sheet 4 + Sheet 6)
export const processBalanceHistoryAndBondRetake = (
  balanceHistoryMap: Record<string, BalanceHistoryEntry[]>,
  customerMap: Record<string, CustomerData>,
  bankInfoMap: Record<string, UserBankInfo>,
  interestEndDate: string,
  purchaseHistoryData: PurchaseHistoryRow[] = [],
): Sheet4And6Result => {
  const balanceHistoryRows: BalanceHistoryRow[] = [];
  const bondRetakeRows: BondResultRow[] = [];

  const normalizeDateKey = (dateStr: string): string => {
    if (!dateStr) return "";
    const parsed = moment(dateStr, [
      "YYYY/MM/DD HH:mm:ss",
      "YYYY/MM/DD",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
      "M/D/YYYY",
      "MM/DD/YYYY",
    ], true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
    const asNumber = Number(dateStr);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      const ts = moment(asNumber);
      if (ts.isValid()) return ts.format("YYYY-MM-DD");
    }
    return "";
  };

  const purchaseHistoryEntriesByEmail = new Map<string, {
    amount: number;
    portfolioIndex: number;
    bondAmount: number;
    holdAmount: number;
    purchaseDate: string;
    normalizedDate: string;
  }[]>();
  purchaseHistoryData.forEach((row) => {
    const email = (row.email || "").toLowerCase().trim();
    const dateKey = normalizeDateKey(row.haDate || "");
    const portfolioIndex = Number(row.portfolioIndex) || 0;
    if (!email.includes("@") || !dateKey || portfolioIndex <= 0) return;
    if (!purchaseHistoryEntriesByEmail.has(email)) purchaseHistoryEntriesByEmail.set(email, []);
    purchaseHistoryEntriesByEmail.get(email)!.push({
      amount: Number(row.amount) || 0,
      portfolioIndex,
      bondAmount: Number(row.bond) || 0,
      holdAmount: Number(row.holdAmount) || 0,
      purchaseDate: row.haDate || dateKey,
      normalizedDate: dateKey,
    });
  });

  const findHistoricalPurchaseMatch = (
    email: string,
    purchaseDate: string,
    principleAmount: number,
  ): { amount: number; portfolioIndex: number; bondAmount: number; holdAmount: number; purchaseDate: string } | null => {
    const normalizedPurchaseDate = normalizeDateKey(purchaseDate);
    const rows = purchaseHistoryEntriesByEmail.get(email.toLowerCase().trim()) || [];
    if (rows.length === 0) return null;

    const purchaseMoment = normalizedPurchaseDate ? moment(normalizedPurchaseDate, "YYYY-MM-DD", true) : null;
    const candidates = rows.filter((row) => {
      if (row.normalizedDate === normalizedPurchaseDate) return true;
      if (!purchaseMoment) return false;
      const rowMoment = moment(row.normalizedDate, "YYYY-MM-DD", true);
      if (!rowMoment.isValid()) return false;
      return Math.abs(rowMoment.diff(purchaseMoment, "days")) <= 1;
    });
    if (candidates.length === 0) return null;
    if (candidates.length === 1) {
      return {
        amount: candidates[0].amount,
        portfolioIndex: candidates[0].portfolioIndex,
        bondAmount: candidates[0].bondAmount,
        holdAmount: candidates[0].holdAmount,
        purchaseDate: candidates[0].purchaseDate,
      };
    }
    const targetAmount = Number(principleAmount) || 0;
    const best = candidates.reduce((acc, current) => {
      if (!acc) return current;
      const currentDateGap = purchaseMoment
        ? Math.abs(moment(current.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      const accDateGap = purchaseMoment
        ? Math.abs(moment(acc.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      const currentAmountGap = Math.abs(current.amount - targetAmount);
      const accAmountGap = Math.abs(acc.amount - targetAmount);
      if (currentDateGap !== accDateGap) return currentDateGap < accDateGap ? current : acc;
      return currentAmountGap < accAmountGap ? current : acc;
    }, candidates[0]);
    if (!best) return null;
    return {
      amount: best.amount,
      portfolioIndex: best.portfolioIndex,
      bondAmount: best.bondAmount,
      holdAmount: best.holdAmount,
      purchaseDate: best.purchaseDate,
    };
  };

  const findBondPurchaseMatch = (
    email: string,
    purchaseDate: string,
    bondAmount: number,
  ): { amount: number; portfolioIndex: number; bondAmount: number; holdAmount: number; purchaseDate: string } | null => {
    const normalizedPurchaseDate = normalizeDateKey(purchaseDate);
    const rows = purchaseHistoryEntriesByEmail.get(email.toLowerCase().trim()) || [];
    const targetBondAmount = Number(bondAmount) || 0;
    if (rows.length === 0 || targetBondAmount <= 0) return null;

    const exactBondRows = rows.filter((row) => Math.abs(row.bondAmount - targetBondAmount) <= 1);
    if (exactBondRows.length === 0) return null;

    const purchaseMoment = normalizedPurchaseDate ? moment(normalizedPurchaseDate, "YYYY-MM-DD", true) : null;
    const best = exactBondRows.reduce((acc, current) => {
      if (!acc) return current;
      const currentDateGap = purchaseMoment
        ? Math.abs(moment(current.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      const accDateGap = purchaseMoment
        ? Math.abs(moment(acc.normalizedDate, "YYYY-MM-DD", true).diff(purchaseMoment, "days"))
        : 0;
      if (currentDateGap !== accDateGap) return currentDateGap < accDateGap ? current : acc;
      const currentAmountGap = Math.abs(current.amount - targetBondAmount);
      const accAmountGap = Math.abs(acc.amount - targetBondAmount);
      return currentAmountGap < accAmountGap ? current : acc;
    }, exactBondRows[0]);

    return {
      amount: best.amount,
      portfolioIndex: best.portfolioIndex,
      bondAmount: best.bondAmount,
      holdAmount: best.holdAmount,
      purchaseDate: best.purchaseDate,
    };
  };

  Object.entries(balanceHistoryMap).forEach(([email, balanceHistory]) => {
    const customer = customerMap[email];
    const userInfo = bankInfoMap[email] || {};
    const userCashTotal = userInfo.cashTotal || 0;

    balanceHistory.forEach(purchase => {
      const apiBondAmount = purchase.bonds?.reduce((sum, b) => sum + (b.principleAmount || 0), 0) || 0;
      const purchaseDateTimeStr = moment(Number(purchase.createdAt)).format("YYYY/MM/DD HH:mm:ss");
      const purchaseDateStr = moment(Number(purchase.createdAt)).format("YYYY/MM/DD");
      const historicalMatch = findHistoricalPurchaseMatch(
        email,
        purchaseDateStr,
        purchase.principleAmount || 0,
      );
      const historicalPurchaseDate = historicalMatch?.purchaseDate || purchaseDateStr;
      const totalAmount = historicalMatch ? (Number(historicalMatch.amount) || 0) : (purchase.principleAmount || 0);
      const portfolioIndex = historicalMatch?.portfolioIndex ?? (userInfo.portfolioIndex || 0);
      const bondAmount = historicalMatch ? (Number(historicalMatch.bondAmount) || 0) : apiBondAmount;
      let tag = getTagFromRatio(bondAmount, totalAmount);

      if (tag === "Normal" && userCashTotal > 0) {
        tag = "Бэлэн мөнгө шалгах";
      }

      const ratio = totalAmount > 0 ? bondAmount / totalAmount : 0;
      const holdAmount = historicalMatch ? (Number(historicalMatch.holdAmount) || 0) : (purchase.holdAmount || 0);

      const historyRow: BalanceHistoryRow = {
        createdAt: moment(Number(purchase.createdAt)).format("YYYY/MM/DD HH:mm:ss"),
        amount: totalAmount,
        account: userInfo.accountNo || "",
        purchaseDate: historicalPurchaseDate,
        lastName: customer?.lastName || "",
        firstName: customer?.firstName || "",
        regNumber: customer?.regNumber || "",
        phone: customer?.phone || "",
        email,
        txCode: userInfo.depositCode || "",
        totalAmount: totalAmount,
        portfolioIndex,
        bondAmount: bondAmount,
        bondRatio: ratio,
        holdAmount,
        stocks: purchase.stocks?.map(s => ({
          symbol: s.stock?.symbol || "",
          amount: s.totalPrice || 0,
          quantity: s.totalAmount || 0,
        })) || [],
        tag,
        purchaseTitle: purchase.title || "",
      };

      balanceHistoryRows.push(historyRow);

      // Add to Sheet 6 — include all balance history entries that have bonds
      {
        if (purchase.bonds && purchase.bonds.length > 0) {
          purchase.bonds.forEach(bond => {
            const bondSpecificMatch = findBondPurchaseMatch(
              email,
              purchaseDateStr,
              bond.principleAmount || 0,
            );
            const bondPurchaseDate = bondSpecificMatch?.purchaseDate || historicalPurchaseDate;
            const matchedPurchaseDateRaw = String(
              bondSpecificMatch?.purchaseDate || historicalMatch?.purchaseDate || "",
            );
            const bondPurchaseDateTime =
              /\d{1,2}:\d{2}:\d{2}/.test(matchedPurchaseDateRaw) ||
              /\d{1,2}:\d{2}/.test(matchedPurchaseDateRaw)
                ? matchedPurchaseDateRaw
                : purchaseDateTimeStr;
            bondRetakeRows.push({
              email,
              phone: customer?.phone || "",
              lastName: customer?.lastName || "",
              firstName: customer?.firstName || "",
              regNumber: customer?.regNumber || "",
              bankAccount: userInfo.accountNo || "",
              bank: userInfo.bankName || "",
              txCode: userInfo.depositCode || "",
              ttd: customer?.ttd || "",
              name: bond.bond?.name || "",
              amount: bond.principleAmount || 0,
              bondRate: 0,
              purchaseDate: bondPurchaseDate,
              sourcePurchaseDate: bondPurchaseDate,
              sourcePurchaseDateTime: bondPurchaseDateTime,
              interestEndDate: interestEndDate,
              days: 0,
              calculatedInterest: 0,
              tax30: 0,
              totalPrice: bond.principleAmount || 0,
              avtamataFee: bond.fee || 0,
              oat10: 0,
              transferAmount: bond.principleAmount || 0,
              tag: tag,
              purchaseTitle: purchase.title || "",
            });
          });
        }
      }
    });
  });

  return { balanceHistoryRows, bondRetakeRows };
};

// Prepare bond retake rows with tailbar for Sheet 6
export const prepareBondRetakeWithTailbar = (
  bondRetakeRows: BondResultRow[]
): BondRetakeRowWithTailbar[] => {
  return bondRetakeRows.map(row => {
    const originalBondName = row.name || "";
    const bondName = (originalBondName === "GSB-ABS." || originalBondName === "GSB-ABS") ? "GSB-ABS ХБҮЦ" : originalBondName;
    const tailbar = `${bondName}-н хүү төлөлт`;
    return { ...row, tailbar };
  });
};
